import {useState} from 'react'
import {StyleSheet, View} from 'react-native'

import {AccordionSlidePlay} from '#/components/capsules/play/slides/AccordionSlidePlay'
import {ComplexFillBlankSlidePlay} from '#/components/capsules/play/slides/ComplexFillBlankSlidePlay'
import {ContentSlidePlay} from '#/components/capsules/play/slides/ContentSlidePlay'
import {DragWordsSlidePlay} from '#/components/capsules/play/slides/DragWordsSlidePlay'
import {FillBlankSlidePlay} from '#/components/capsules/play/slides/FillBlankSlidePlay'
import {FillBlanksSlidePlay} from '#/components/capsules/play/slides/FillBlanksSlidePlay'
import {FindWordsSlidePlay} from '#/components/capsules/play/slides/FindWordsSlidePlay'
import {ImageChoiceSlidePlay} from '#/components/capsules/play/slides/ImageChoiceSlidePlay'
import {MarkWordsSlidePlay} from '#/components/capsules/play/slides/MarkWordsSlidePlay'
import {MatchingSlidePlay} from '#/components/capsules/play/slides/MatchingSlidePlay'
import {MultiChoiceSlidePlay} from '#/components/capsules/play/slides/MultiChoiceSlidePlay'
import {OrderingSlidePlay} from '#/components/capsules/play/slides/OrderingSlidePlay'
import {SingleChoiceSlidePlay} from '#/components/capsules/play/slides/SingleChoiceSlidePlay'
import {TrueFalseSlidePlay} from '#/components/capsules/play/slides/TrueFalseSlidePlay'
import {type Slide} from '#/types/capsule'
import {FeedbackBar} from './FeedbackBar'
import {type FBState, type SlideAnswerResult} from './types'

type Props = {
  slide: Slide
  primaryColor: string
  isPreview?: boolean
  onAnswer?: (result: SlideAnswerResult) => void
}

export function SlidePlayerRenderer({
  slide,
  primaryColor,
  isPreview = false,
  onAnswer,
}: Props) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [fbState, setFbState] = useState<FBState>('correct')
  const [fbResult, setFbResult] = useState<SlideAnswerResult>({
    isCorrect: true,
    points: 0,
    maxPoints: 0,
  })
  const [retryKey, setRetryKey] = useState(0)

  const handleAnswer = (result: SlideAnswerResult) => {
    const state: FBState = result.isCorrect
      ? 'correct'
      : result.points > 0
        ? 'partial'
        : 'wrong'
    setFbState(state)
    setFbResult(result)
    setShowFeedback(true)
    onAnswer?.(result)
  }

  const handleRetry = () => {
    setShowFeedback(false)
    setRetryKey(k => k + 1)
  }

  const handleShowSolution = () => {
    setFbState('solution')
  }

  // These types embed FeedbackBar internally and manage their own feedback lifecycle
  const SELF_FEEDBACK_TYPES = [
    'fill_blanks',
    'mark_words',
    'drag_words',
    'complex_fill_blank',
    'find_words',
  ]
  const hasSelfFeedback = SELF_FEEDBACK_TYPES.includes(slide.type)

  const slideProps = {
    slide,
    onAnswer: handleAnswer,
    showFeedback,
    feedbackState: fbState,
    primaryColor,
    isPreview,
    retryKey,
  }

  let content: React.ReactNode = null
  switch (slide.type) {
    case 'content':
      content = <ContentSlidePlay {...slideProps} />
      break
    case 'true_false':
      content = <TrueFalseSlidePlay {...slideProps} />
      break
    case 'single_choice':
      content = <SingleChoiceSlidePlay {...slideProps} />
      break
    case 'multiple_choice':
      content = <MultiChoiceSlidePlay {...slideProps} />
      break
    case 'image_choice':
      content = <ImageChoiceSlidePlay {...slideProps} />
      break
    case 'matching':
      content = <MatchingSlidePlay {...slideProps} />
      break
    case 'ordering':
      content = <OrderingSlidePlay {...slideProps} />
      break
    case 'fill_blank':
      content = <FillBlankSlidePlay {...slideProps} />
      break
    case 'fill_blanks':
      content = <FillBlanksSlidePlay {...slideProps} />
      break
    case 'accordion':
      content = <AccordionSlidePlay {...slideProps} />
      break
    case 'drag_words':
      content = <DragWordsSlidePlay {...slideProps} />
      break
    case 'mark_words':
      content = <MarkWordsSlidePlay {...slideProps} />
      break
    case 'find_words':
      content = <FindWordsSlidePlay {...slideProps} />
      break
    case 'complex_fill_blank':
      content = <ComplexFillBlankSlidePlay {...slideProps} />
      break
    case 'video':
    default:
      return null
  }

  return (
    <View style={ss.root}>
      {content}
      {showFeedback && !hasSelfFeedback && (
        <View style={ss.feedbackWrap}>
          <FeedbackBar
            state={fbState}
            points={fbResult.points}
            maxPoints={fbResult.maxPoints}
            explanation={fbResult.explanation}
            solutionText={fbResult.solutionText}
            onRetry={fbState !== 'correct' ? handleRetry : undefined}
            onShowSolution={
              fbState === 'wrong' || fbState === 'partial'
                ? handleShowSolution
                : undefined
            }
          />
        </View>
      )}
    </View>
  )
}

const ss = StyleSheet.create({
  root: {
    flex: 1,
  },
  feedbackWrap: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
})
